'use client';
import React, { useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import Sidebar, { MODULES, SubMenuItem } from './Sidebar';
import Topbar from './Topbar';
import Link from 'next/link';



interface AppLayoutProps {
  children: React.ReactNode;
  pageTitle?: string;
  pageBreadcrumb?: string;
  activeModuleId?: string;
}

function SubMenuGrid({ subMenus }: { subMenus: SubMenuItem[] }) {
  return (
    <div className="p-4 border-b" style={{ background: '#F4F6F8', borderColor: '#e5e7eb' }}>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
        {subMenus.map((item, idx) => {
          const ItemIcon = item.icon;
          const isExternal = item.external;
          const content = (
            <div
              key={idx}
              className="flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl border bg-white cursor-pointer transition-all duration-150 group relative"
              style={{ borderColor: '#e5e7eb', minWidth: 0 }}
              onMouseEnter={e => {
                const el = e.currentTarget;
                el.style.background = '#1B4F72';
                el.style.borderColor = '#1B4F72';
                el.querySelectorAll('span').forEach(s => (s as HTMLElement).style.color = '#fff');
                el.querySelectorAll('svg').forEach(s => (s as SVGElement).style.color = '#fff');
              }}
              onMouseLeave={e => {
                const el = e.currentTarget;
                el.style.background = '#fff';
                el.style.borderColor = '#e5e7eb';
                el.querySelectorAll('span').forEach(s => (s as HTMLElement).style.color = '');
                el.querySelectorAll('svg').forEach(s => (s as SVGElement).style.color = '');
              }}
            >
              <ItemIcon size={20} style={{ color: '#1B4F72' }} />
              <span className="text-xs font-medium text-center leading-tight" style={{ color: '#1B4F72' }}>
                {item.label}
              </span>
              {item.badge && (
                <span
                  className="absolute top-1 right-1 text-[9px] font-bold rounded-full px-1 py-0.5 text-white"
                  style={{ background: '#C0392B' }}
                >
                  {item.badge}
                </span>
              )}
              {isExternal && (
                <span
                  className="absolute top-1 left-1 text-[8px] font-bold rounded px-1 py-0.5 text-white"
                  style={{ background: '#D4AC0D' }}
                >
                  EXT
                </span>
              )}
            </div>
          );

          if (isExternal && item.href) {
            return (
              <a key={idx} href={item.href} target="_blank" rel="noopener noreferrer" className="no-underline">
                {content}
              </a>
            );
          }
          if (item.href) {
            return (
              <Link key={idx} href={item.href} className="no-underline">
                {content}
              </Link>
            );
          }
          return content;
        })}
      </div>
    </div>
  );
}

const themeStyles: Record<string, { bg: string; mainBg: string; topbarBg: string; topbarBorder: string; text: string }> = {
  light: {
    bg: '#F4F6F8',
    mainBg: '#fff',
    topbarBg: '#fff',
    topbarBorder: '#e5e7eb',
    text: '#1B4F72',
  },
  dark: {
    bg: '#1a1a2e',
    mainBg: '#16213e',
    topbarBg: '#0f3460',
    topbarBorder: '#1a1a2e',
    text: '#e2e8f0',
  },
  institutional: {
    bg: '#F0F4F8',
    mainBg: '#EBF5FB',
    topbarBg: '#1B4F72',
    topbarBorder: '#154360',
    text: '#fff',
  },
};

export default function AppLayout({ children, pageTitle, pageBreadcrumb, activeModuleId }: AppLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, setTheme] = useState('light');
  const pathname = usePathname();

  const getDefaultActiveModule = () => {
    if (activeModuleId) return activeModuleId;
    const found = MODULES.find(m => {
      if (m.href === '/' && pathname === '/') return true;
      if (m.href !== '/' && pathname.startsWith(m.href)) return true;
      return false;
    });
    return found?.id || 'dashboard';
  };

  const [selectedModuleId, setSelectedModuleId] = useState<string>(getDefaultActiveModule());

  const selectedModule = MODULES.find(m => m.id === selectedModuleId);
  const router = useRouter();
  const ts = themeStyles[theme] || themeStyles.light;

  const handleModuleSelect = (moduleId: string) => {
    setSelectedModuleId(moduleId);
    const mod = MODULES.find(m => m.id === moduleId);
    if (mod) {
      router.push(mod.href);
    }
    setMobileOpen(false);
  };

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: ts.bg }}>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar desktop */}
      <div className="hidden lg:flex shrink-0">
        <Sidebar
          collapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
          activeModuleId={selectedModuleId}
          onModuleSelect={handleModuleSelect}
        />
      </div>

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-40 lg:hidden transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Sidebar
          collapsed={false}
          onToggle={() => setMobileOpen(false)}
          activeModuleId={selectedModuleId}
          onModuleSelect={handleModuleSelect}
        />
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          onMobileMenuToggle={() => setMobileOpen(!mobileOpen)}
          pageTitle={pageTitle || selectedModule?.label}
          pageBreadcrumb={pageBreadcrumb || 'Poste de Travail DG — Cadastre Minier Numérique'}
          theme={theme}
          onThemeChange={setTheme}
        />

        {/* Sub-menu buttons bar */}
        {selectedModule && selectedModule.subMenus.length > 0 && (
          <SubMenuGrid subMenus={selectedModule.subMenus} />
        )}

        <main className="flex-1 overflow-y-auto" style={{ background: ts.bg }}>
          {children}
        </main>
      </div>
    </div>
  );
}